import { Injectable, inject } from '@angular/core';
import { ApiKey, ApiKeyStatus, BackofficeApiKey, CreatedApiKey } from '../models/api-key.model';
import { SupabaseService } from './supabase.service';

@Injectable({ providedIn: 'root' })
export class ApiKeysService {
  private readonly supabase = inject(SupabaseService);

  async list(): Promise<ApiKey[]> {
    const accessToken = await this.getAccessToken();
    const { data, error } = await this.supabase.instance.functions.invoke('client-consult-api-keys', {
      body: {},
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    });

    if (error) {
      const message = await this.extractFunctionErrorMessage(error, 'No se pudieron cargar las API Keys.');
      throw new Error(this.toFriendlyError(message));
    }

    const response = this.toRecord(data);
    if (response['success'] !== true) {
      throw new Error(this.toFriendlyError(this.toSafeString(response['error']) || 'No se pudieron cargar las API Keys.'));
    }

    return this.extractProviderRows(response).map((row) => this.mapProviderApiKey(row));
  }

  async create(name: string): Promise<CreatedApiKey> {
    const accessToken = await this.getAccessToken();
    const { data, error } = await this.supabase.instance.functions.invoke('client-register-api-key', {
      body: {
        nombre: name.trim()
      },
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    });

    if (error) {
      const message = await this.extractFunctionErrorMessage(error, 'No se pudo crear la API Key.');
      throw new Error(this.toFriendlyError(message));
    }

    const response = this.toRecord(data);
    if (response['success'] !== true) {
      throw new Error(this.toFriendlyError(this.toSafeString(response['error']) || 'No se pudo crear la API Key.'));
    }

    return {
      success: true,
      message: 'API Key creada.'
    };
  }

  async revoke(apiKey: string, name?: string): Promise<void> {
    const accessToken = await this.getAccessToken();
    const { data, error } = await this.supabase.instance.functions.invoke('client-delete-api-key', {
      body: {
        apiKey,
        nombre: name?.trim() || undefined
      },
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    });

    if (error) {
      const message = await this.extractFunctionErrorMessage(error, 'No se pudo eliminar la API Key.');
      throw new Error(this.toFriendlyError(message));
    }

    const response = this.toRecord(data);
    if (response['success'] !== true) {
      throw new Error(this.toFriendlyError(this.toSafeString(response['error']) || 'No se pudo eliminar la API Key.'));
    }
  }

  async listBackoffice(): Promise<BackofficeApiKey[]> {
    throw new Error('Legacy admin API Keys flow disabled. Use BackofficeService provider API Keys methods.');
  }

  async adminRevoke(_id: string): Promise<void> {
    throw new Error('Legacy admin API Keys flow disabled. Use BackofficeService provider API Keys methods.');
  }

  async providerRegister(_ruc: string, _nombre: string): Promise<ProviderApiKeyEdgeResult> {
    throw new Error('Legacy admin API Keys flow disabled. Use BackofficeService provider API Keys methods.');
  }

  async providerDelete(_ruc: string, _payload: { nombre?: string; id?: string; apiKey?: string }): Promise<ProviderApiKeyEdgeResult> {
    throw new Error('Legacy admin API Keys flow disabled. Use BackofficeService provider API Keys methods.');
  }

  async providerConsult(_ruc: string): Promise<ProviderApiKeyEdgeResult> {
    throw new Error('Legacy admin API Keys flow disabled. Use BackofficeService provider API Keys methods.');
  }

  async providerList(): Promise<ProviderApiKeyEdgeResult> {
    throw new Error('Legacy admin API Keys flow disabled. Use BackofficeService provider API Keys methods.');
  }

  private mapProviderApiKey(value: unknown): ApiKey {
    const row = this.toRecord(value);
    const name = this.toSafeString(row['nombre'] ?? row['name']);
    const key = this.toSafeString(row['apikey'] ?? row['apiKey'] ?? row['api_key']);
    const providerStatus = this.toSafeString(row['estado'] ?? row['status']);
    const createdAt = this.toSafeString(row['fechaCreacion'] ?? row['fecha_creacion'] ?? row['created_at']);
    const status = this.mapProviderStatus(providerStatus);
    const id = this.toSafeString(row['id']) || `${name}-${createdAt}-${key.slice(-8)}`;

    return {
      id,
      name,
      key,
      status,
      created_at: createdAt,
      last_used_at: null,
      provider_status: providerStatus,
      keyPrefix: key ? `${key.slice(0, 6)}...` : '',
      scopes: [],
      isActive: status === 'active',
      lastUsedAt: null,
      expiresAt: null,
      revokedAt: status === 'revoked' ? createdAt || new Date().toISOString() : null,
      createdAt: createdAt || new Date().toISOString(),
      updatedAt: null,
      rateLimitPerMinute: 0,
      rateLimitPerDay: 0,
      description: null
    };
  }

  private mapProviderStatus(value: string): ApiKeyStatus {
    const normalized = value.trim().toLowerCase();
    if (normalized.includes('revoc') || normalized.includes('elimin') || normalized.includes('deleted')) {
      return 'revoked';
    }

    if (normalized === 'activa' || normalized === 'activo' || normalized === 'active') {
      return 'active';
    }

    return 'inactive';
  }

  private extractProviderRows(value: unknown): unknown[] {
    const row = this.toRecord(value);
    const directData = row['data'];
    const result = this.toRecord(row['result']);
    const resultData = result['data'];

    if (Array.isArray(directData)) {
      return directData;
    }

    if (Array.isArray(resultData)) {
      return resultData;
    }

    if (directData) {
      return [directData];
    }

    if (resultData) {
      return [resultData];
    }

    return [];
  }

  private async getAccessToken(): Promise<string> {
    const { data, error } = await this.supabase.instance.auth.getSession();

    if (error || !data.session?.access_token) {
      throw new Error('No se pudo validar la sesión.');
    }

    return data.session.access_token;
  }

  private async extractFunctionErrorMessage(error: unknown, fallbackMessage: string): Promise<string> {
    const fallback = error instanceof Error ? error.message : fallbackMessage;
    const context = (error as { context?: Response })?.context;

    if (!context) {
      return fallback;
    }

    try {
      const payload = await context.clone().json() as {
        error?: string;
        message?: string;
      };

      return payload.error || payload.message || fallback;
    } catch {
      return fallback;
    }
  }

  private toFriendlyError(message: string): string {
    if (message.includes('PROFILE_INACTIVE')) {
      return 'Tu cuenta está inactiva. No puedes crear API Keys.';
    }

    if (message.includes('API_KEY_NAME_REQUIRED')) {
      return 'Ingresa un nombre para la API Key.';
    }

    if (message.includes('API_KEY_NAME_TOO_LONG')) {
      return 'El nombre de la API Key es demasiado largo.';
    }

    if (message.includes('API_KEY_NOT_FOUND')) {
      return 'API Key no encontrada.';
    }

    if (message.includes('NOT_AUTHORIZED')) {
      return 'No tienes permisos para realizar esta acción.';
    }

    return message;
  }

  private toRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' && !Array.isArray(value)
      ? value as Record<string, unknown>
      : {};
  }

  private toSafeString(value: unknown): string {
    return typeof value === 'string' ? value : '';
  }
}

export interface ProviderApiKeyEdgeResult {
  success: boolean;
  provider: string;
  result: Record<string, unknown>;
}
